import { inject, Injectable, signal, computed, OnDestroy } from '@angular/core';
import WebPhone from 'ringcentral-web-phone';
import { SocketsService } from './socket.service';
import { BehaviorSubject, Subscription } from 'rxjs';
import { localStorageService } from './localstorage.service';
import { IncomingCallService } from './incoming-call.services';
import { ApiService } from './api.service';
import { SharedService } from './shared.service';
// import { environment } from 'src/environments/environment'
// import RingCentral from "@rc-ex/core";
import { ActivatedRoute, Router } from '@angular/router';

export enum CallState {
    IDLE = 'IDLE',
    DIALING = 'DIALING',
    RINGING = 'RINGING',
    CONNECTED = 'CONNECTED',
    CONFERENCE = 'CONFERENCE',
    ENDED = 'ENDED',
    FAILED = 'FAILED'
}

export interface CallSession {
    id: string;
    session: any;
    type: 'inbound' | 'outbound';
    state: CallState;
    isMuted: boolean;
    isOnHold: boolean;
    callDuration: number;
    timerInterval: any;
    caller_data: any;
    onCallUserDetails: any;
    startTime: number;
}

@Injectable({
    providedIn: 'root',
})
export class RingcentralWebphone implements OnDestroy {
    private websocketService = inject(SocketsService);
    private _shared = inject(SharedService);
    private incomingCallService = inject(IncomingCallService);
    localStorageService = inject(localStorageService);
    private _api = inject(ApiService);

    public webPhone: any;
    callState = signal<CallState>(CallState.IDLE);
    inboundCallState = signal<CallState>(CallState.IDLE);
    callInProgress: boolean = false;
    isLoadingSip = signal(false);
    sipError = signal<string | null>(null);
    callended$: BehaviorSubject<any> = new BehaviorSubject(false);
    timer$: BehaviorSubject<any> = new BehaviorSubject(0);
    currentSession: any;
    public autoTimeoutRef: any = null;
    public router = inject(Router);
    public route = inject(ActivatedRoute)
    private ringtoneAudio: HTMLAudioElement | null = new Audio('/assets/incoming_call.mp3');
    activeSession: Record<string, any> = {};
    private readonly sipInfoCacheKey = 'rc_sip_info_cache';
    rc: any;
    rcToken: any;
    conferenceSession: any = [];
    currentConferenceSessionId: string | null = null;
    private readonly jwtCacheKey = 'rc_jwt_cache';
    isReconnecting: any;
    sessionWatcher: any;
    refreshToken: string | undefined;

    private websocketSubscription: Subscription | null = null;
    profileData: any;
    activeSessions = signal<Record<string, CallSession>>({});
    activeSessionsList = computed(() => Object.values(this.activeSessions()));
    isInCall = computed(() => Object.keys(this.activeSessions()).length > 0);

    sipInfo: any;
    isMuted = signal(false);
    isOnHold = signal(false);
    onCallUserDetails: any;
    caller_data: any;
    incomingCallSession: any;
    private deviceChangeTimeout: any;
    private sessionIdCounter = 0;
    private endingSessionIds = new Set<string>();

    constructor() {
        this.registerUnloadListeners();
        this.registerAudioInputDeviceListener();
    }

    ngOnDestroy() {
        this.stopTimer();
        this.websocketSubscription?.unsubscribe();
        this.websocketService.disconnect();
        if (this.deviceChangeTimeout) {
            clearTimeout(this.deviceChangeTimeout);
        }
        navigator.mediaDevices.removeEventListener('devicechange', this.handleDeviceChange);
    }

    formattedTime(timeInSeconds: number): string {
        const minutes = Math.floor(timeInSeconds / 60).toString().padStart(2, '0');
        const seconds = (timeInSeconds % 60).toString().padStart(2, '0');
        return `${minutes}:${seconds}`;
    }

    public handleDeviceChange = async (e: any) => {
    console.warn("Hardware change detected! Re-scanning devices...");
    if (this.deviceChangeTimeout) {
      clearTimeout(this.deviceChangeTimeout);
    }
    // Wait 500ms before checking the actual state
    this.deviceChangeTimeout = setTimeout(async () => {
      const devices = await navigator.mediaDevices.enumerateDevices();
      const physicalMics = devices.filter(d => 
        d.kind === 'audioinput' && 
        d.deviceId !== 'default' && 
        d.deviceId !== 'communications'
      );
      console.log('physicalMics => ', physicalMics);
      const isCallActive = this.callState() === CallState.CONNECTED || this.callState() === CallState.RINGING || this.callState() === CallState.CONFERENCE
      if((!physicalMics.length || physicalMics.length == 0) && isCallActive) this._shared.customPopups('Microphone disconnected. Customer cannot hear you.', 1);

      if(physicalMics.length && isCallActive) {
        navigator.mediaDevices.getUserMedia({ audio: true })
        .then(async (newStream) => {
          const newTrack = newStream.getAudioTracks()[0];
          
          const pc: RTCPeerConnection = (this.currentSession as any).rtcPeerConnection;

          if (pc) {
            const senders = pc.getSenders();
            const audioSender = senders.find(s => s.track && s.track.kind === 'audio');

            if (audioSender) {
              await audioSender.replaceTrack(newTrack);
              
              if ((this.currentSession as any)._mediaStream) {
                (this.currentSession as any)._mediaStream = newStream;
              }

              this._shared.customPopups('Microphone recovered successfully.', 0);
              console.log('Audio track replaced and session stream updated.');
            }
          }
        })
        .catch((err) => {
          console.error('Error re-acquiring mic stream:', err);
          this._shared.customPopups('Could not reactivate microphone.', 2);
        });
      }
    }, 500);
  }
    private registerAudioInputDeviceListener(): void {
        navigator.mediaDevices.addEventListener('devicechange', this.handleDeviceChange);
    }

    private registerUnloadListeners() {
        window.addEventListener('beforeunload', (event) => {
            if (this.isInCall()) {
                this.endCall(this.currentSession);
                event.preventDefault();
            }
        });
    }

    async init(rawSipInfo: any) {
        try {
            if (!rawSipInfo || !rawSipInfo.domain) throw new Error('Invalid SIP info');
            this.webPhone = new WebPhone({ sipInfo: rawSipInfo, debug: true });
            await this.webPhone.start();
            this.registerRingCentralEvents();
        } catch (e) {
            console.error('WebPhone init error', e);
        }
    }

    public getSessionId(session: any): string {
        if (!session) return '';
        if (typeof session === 'string') return session;
        let id = session.id || session.session_id || session.sessionId || session._id;
        if (!id) {
            if (!session._internalId) {
                session._internalId = `session_${Date.now()}_${this.sessionIdCounter++}`;
            }
            id = session._internalId;
        }
        return id.toString();
    }

    private registerRingCentralEvents() {
        this.webPhone.on('inboundCall', async (session: any) => {
            const id = this.getSessionId(session);
            console.log('Inbound call received:', id);
            this.callState.set(CallState.RINGING);
            this.inboundCallState.set(CallState.RINGING);
            this.currentSession = session;
            this.caller_data = { caller: session?.caller || session?.remoteNumber?.replace(/^\+1/, '') || 'Unknown' };

            this.initWebSocket(session, this.caller_data, false, 'inbound');
            this.attachSessionListeners(session); // FIX: Attach listeners for inbound too
            this.incomingCallService.setIncomingCall(session);
            this.incomingCallSession = session;
        });
    }

    stopRingtone(force = false) {
        if (!this.ringtoneAudio) return;

        try {
            this.ringtoneAudio.pause();
            this.ringtoneAudio.currentTime = 0;

            // 🔥 HARD RESET - completely destroy and recreate audio element
            if (force) {
                this.ringtoneAudio.src = '';
                this.ringtoneAudio.load();
                this.ringtoneAudio = null;
            }

        } catch (e) {
            console.error("Stop ringtone error", e);
        }
    }

    startAutoTimeout(session: any) {
        this.clearAutoTimeout();

        this.autoTimeoutRef = setTimeout(() => {
            if (this.currentSession !== session) return;

            const status = session?.status || session?.state;

            if (!['terminated', 'rejected', 'failed'].includes(status)) {
                console.log("⏱️ Auto terminating stale call");

                try {
                    session.terminate();
                } catch (e) {
                    console.error("Terminate error", e);
                }
            }

            this.cleanupSession();

        }, 28500);
    }
    clearAutoTimeout() {
        if (this.autoTimeoutRef) {
            clearTimeout(this.autoTimeoutRef);
            this.autoTimeoutRef = null;
        }
    }
    handleSessionEvents(session: any) {

        const endCall = () => {
            this.stopRingtone(true); // 🔥 force stop
            this.endCall(session);
            this.cleanupSession();
        };

        session.on('progress', () => {
            console.log("📡 Ringing...");
        });

        session.on('accepted', () => {
            console.log("✅ Call answered");
            this.stopRingtone(); // normal stop
        });

        session.on('terminated', endCall);
        session.on('rejected', endCall);
        session.on('cancel', endCall);
        session.on('failed', endCall);
        session.on('disposed', endCall);
        session.on('dispose', endCall);
    }
    cleanupSession() {
        console.log("🧹 Cleaning session");
        try {
            this.clearAutoTimeout();
            this.stopRingtone(true);
            this.inboundCallState.set(CallState.IDLE);
            this.incomingCallService.clearIncomingCall();
            this.incomingCallSession = null;
        } catch (e) {
            console.error("Cleanup error", e);
        }
    }

    private initializeSessionHandlers(session: any, type: 'inbound' | 'outbound', caller_data?: any) {
        const id = this.getSessionId(session);
        console.log(`🚀 Initializing handlers for session: ${id} (${type})`);

        // 1. Initial State Sync
        this.initWebSocket(session, caller_data, false, type);
        
        if (type === 'outbound') {
            this.callState.set(CallState.RINGING);
            this.startTimer(id);
        } else {
            this.inboundCallState.set(CallState.RINGING);
        }

        // 2. SIP Listeners
        session.on('progress', () => {
            console.log('📡 SIP progress:', id);
            this.updateSessionState(id, { state: CallState.RINGING });
            this.attachAudio(session);
        });

        session.on('accepted', () => {
            console.log('✅ SIP accepted:', id);
            this.stopRingtone(true);
            this.clearAutoTimeout();
            this.updateSessionState(id, { state: CallState.CONNECTED });
            this.attachAudio(session);
            
            if (this.getSessionId(this.currentSession) === id) {
                this.callState.set(CallState.CONNECTED);
            }

            if (type === 'inbound') {
                this.incomingCallService.clearIncomingCall();
                this.incomingCallSession = null;
                this.startTimer(id);
            }
        });

        const endCallHandler = (reason?: any) => {
            console.log(`🔌 SIP End (${reason || 'terminated'}):`, id);
            this.stopRingtone(true);
            this.clearAutoTimeout();
            
            // Clear incoming call modal for inbound calls
            if (type === 'inbound' || session.direction === 'Inbound') {
                this.inboundCallState.set(CallState.IDLE);
                this.incomingCallService.clearIncomingCall();
                this.incomingCallSession = null;
            }
            
            this.endCall(session);
            this.cleanupSession();
        };

        session.on('terminated', () => endCallHandler('terminated'));
        session.on('rejected', () => endCallHandler('rejected'));
        session.on('cancel', () => endCallHandler('cancel'));
        session.on('failed', (e: any) => {
            console.error('❌ SIP failed:', id, e);
            endCallHandler('failed');
        });
        session.on('disposed', () => endCallHandler('disposed'));
        session.on('bye', () => endCallHandler('bye'));

        // 3. Audio Stream Listener
        if (session?.sessionDescriptionHandler) {
            session.on('trackAdded', () => {
                const sessionData = this.activeSessions()[id];
                if (!sessionData || sessionData.isOnHold) return;

                const pc = session.sessionDescriptionHandler.peerConnection;
                const remoteStream = new MediaStream();
                pc.getReceivers().forEach((r: any) => r.track && remoteStream.addTrack(r.track));
                
                const audio = document.getElementById('remoteAudio') as HTMLAudioElement;
                if (audio) {
                    audio.srcObject = remoteStream;
                    audio.play().catch(() => {});
                }
            });
        }
    }

    public initWebSocket(session: any, caller_data: any, isFromConference?: boolean, type?: 'inbound' | 'outbound'): void {
        let id = this.getSessionId(session);
        const telephonySessionId = session?.sessionId;
        
        console.log('--- initWebSocket triggered ---', { id, telephonySessionId, direction: session.direction });
        
        this.activeSessions.update(sessions => {
            if (sessions[id] && sessions[id].session !== session) {
                console.warn('ID COLLISION DETECTED! Overwriting or collision found for ID:', id);
                // If it's a different session object, we MUST have a unique ID
                id = `${id}_${Date.now()}`;
                console.warn('Assigned new unique ID:', id);
            }
            
            const current = sessions[id];
            return {
                ...sessions,
                [id]: {
                    id,
                    session,
                    type: type || current?.type || (session.direction === 'Inbound' ? 'inbound' : 'outbound'),
                    state: current?.state || CallState.RINGING,
                    isMuted: current?.isMuted || false,
                    isOnHold: current?.isOnHold || false,
                    callDuration: current?.callDuration || 0,
                    timerInterval: current?.timerInterval || null,
                    caller_data: caller_data,
                    onCallUserDetails: this.onCallUserDetails,
                    startTime: current?.startTime || Date.now()
                }
            };
        });

        this.currentSession = session;
        this.caller_data = caller_data;

        if (telephonySessionId) {
            this.websocketService.joinCallRoom(telephonySessionId);
            this.startWebSocketSubscription();
        }
        if (session?.direction === 'outbound') {
                this.saveCall(session, '', caller_data);
        }
    }

    public updateSessionDetails(session: any, caller_data: any, onCallUserDetails?: any): void {
        const id = this.getSessionId(session);
        this.activeSessions.update(sessions => {
            if (sessions[id]) {
                return {
                    ...sessions,
                    [id]: {
                        ...sessions[id],
                        caller_data: { ...sessions[id].caller_data, ...caller_data },
                        onCallUserDetails: onCallUserDetails || sessions[id].onCallUserDetails
                    }
                };
            }
            return sessions;
        });
        if (onCallUserDetails) this.onCallUserDetails = onCallUserDetails;
        if (caller_data) this.caller_data = { ...this.caller_data, ...caller_data };
    }

    private startWebSocketSubscription() {
        if (this.websocketSubscription) return;
        this.websocketSubscription = this.websocketService.ringcentralCallUpdate$.subscribe({
            next: (data) => {
                const incomingId = data?.telephonySessionId || data?.sessionId || data?.session_id;
                if (!incomingId) {
                    console.log('Websocket update received without valid session ID. Ignoring.');
                    return;
                }

                console.log('Searching for session to end via websocket update matching ID:', incomingId);
                const sessions = Object.values(this.activeSessions());
                const target = sessions.find(s => 
                    s.session?.sessionId === incomingId || 
                    s.session?.id === incomingId ||
                    s.session?.telephonySessionId === incomingId ||
                    s.id === incomingId
                );
                
                if (target) {
                    console.log('Ending session via websocket update:', target.id, 'Data:', data);
                    this.endCall(target.session);
                } else {
                    console.log('Websocket update received but no matching active session found for ID:', incomingId);
                }
            },
            error: (err) => console.error('Socket sub error:', err)
        });
    }

    callLogin(code: string) {
        this._api.api({ authCode: code }, 'leads/call_login', "get").subscribe({
            next: (response: any) => {
                console.log('Login response:', response);
                if (response.data) {
                    console.log('Login successful');
                    this.init(response.data.sipInfo);
                }
            },
            error: (error) => {
                console.error('Error during login:', error);
            }
        });
    }

    async checkForSip(lead_id?: string): Promise<any> {
        this.isLoadingSip.set(true);
        return new Promise((resolve, reject) => {
            this._api.api({ lead_id }, 'leads/get_sip_info', "get").subscribe({
                next: async (response: any) => {
                    if (!response.redirect_uri) {
                        this.sipInfo = response.data;
                        await this.init(this.sipInfo);
                        this.isLoadingSip.set(false);
                        resolve(this.sipInfo);
                    } else {
                        window.open(response.redirect_uri, '_blank');
                        this.isLoadingSip.set(false);
                        resolve(null);
                    }
                },
                error: (error) => {
                    this.sipError.set(error?.err || 'Failed to get SIP info');
                    this.isLoadingSip.set(false);
                    reject(error);
                }
            });
        });
    }

    async call(number: string, lead_id?: string, sipInfo?: any) {
        const targetSip = sipInfo || this.sipInfo;
        this.profileData = this.localStorageService.customGetItem('profileData');
        let callerId = targetSip?.username;
        if (this.profileData) {
            const data = JSON.parse(this.profileData);
            if (data[0]?.admin_mobile) callerId = this.formatCallerId(data[0].admin_mobile) || callerId;
        }

        try {
            const session = await this.webPhone.call(number, targetSip?.username);
            this.registerOutgoingEvents(session, { lead_id: lead_id });
            return session;
        } catch (err: any) {
            this._shared.customPopups(err.message || 'Call failed', 2);
        }
    }

    registerOutgoingEvents(session: any, caller_data?: any) {
        this.callState.set(CallState.RINGING);
        this.initWebSocket(session, caller_data);
        this.currentSession = session;
        this.attachSessionListeners(session);
        this.startTimer(this.getSessionId(session));
    }

    private attachSessionListeners(session: any) {
        const id = this.getSessionId(session);
        console.log('Attaching SIP listeners for session:', id);

        session.on('progress', () => {
            console.log('SIP progress for session:', id);
            this.callState.set(CallState.RINGING);
            this.updateSessionState(id, { state: CallState.RINGING });
            this.attachAudio(session);
        });

        session.on('accepted', () => {
            const currentSessionData = this.activeSessions()[id];
            console.log('SIP accepted for session:', id, 'Direction:', session.direction, 'Type:', currentSessionData?.type);
            this.callState.set(CallState.CONNECTED);
            this.updateSessionState(id, { state: CallState.CONNECTED });
            this.attachAudio(session);
            
            if (currentSessionData?.type === 'inbound' || session.direction === 'Inbound') {
                 console.log('Starting timer for inbound call from accepted listener:', id);
                 this.startTimer(id);
            }
        });

        session.on('bye', () => {
            console.log('SIP bye for session:', id);
            this.endCall(session);
        });

        session.on('failed', (e: any) => {
            console.log('SIP failed for session:', id, e);
            this.endCall(session);
        });

        session.on('terminated', () => {
            console.log('SIP terminated for session:', id);
            this.endCall(session);
        });

        session.on('cancel', () => {
            console.log('SIP cancel for session:', id);
            this.endCall(session);
        });

        session.on('rejected', () => {
            console.log('SIP rejected for session:', id);
            this.endCall(session);
        });
    }

    updateSessionState(id: string, updates: Partial<CallSession>) {
        this.activeSessions.update(sessions => {
            if (sessions[id]) {
                return { ...sessions, [id]: { ...sessions[id], ...updates } };
            }
            return sessions;
        });
    }

    startTimer(id: string) {
        const session = this.activeSessions()[id];
        if (session?.timerInterval) {
            console.log('Timer already running for session:', id);
            return;
        }

        let duration = 0;
        const interval = setInterval(() => {
            duration++;
            this.updateSessionState(id, { callDuration: duration });
            if (Object.keys(this.activeSessions()).length === 1) this.timer$.next(duration);
        }, 1000);
        this.updateSessionState(id, { timerInterval: interval, callDuration: 0 });
    }

    stopTimer(id?: string) {
        if (!id) return;
        const session = this.activeSessions()[id];
        if (session?.timerInterval) {
            clearInterval(session.timerInterval);
            this.updateSessionState(id, { timerInterval: null });
        }
    }

    endCall(session?: any) {
        if (!session) return;
        const id = this.getSessionId(session);
        
        if (this.endingSessionIds.has(id)) {
            console.log('Skipping endCall: already in progress for session:', id);
            return;
        }
        this.endingSessionIds.add(id);

        const allIds = Object.keys(this.activeSessions());
        console.log('--- endCall triggered ---', { targetId: id, allActiveIds: allIds });

        const sessionData = this.activeSessions()[id];
        const realSession = sessionData?.session || session;

        try {
            if (realSession && typeof realSession.hangup === 'function' && realSession.state !== 'Terminated') {
                console.log('Calling hangup() on session:', id);
                try { realSession.hangup(); } catch (e) { }
            }
            this.stopTimer(id);
            this.stopRingtone(true);
            
            // Clear incoming call modal if this is the inbound call session
            if (this.incomingCallSession && this.getSessionId(this.incomingCallSession) === id) {
                this.incomingCallService.clearIncomingCall();
                this.incomingCallSession = null;
            }

            let updatedSessions: Record<string, CallSession> = {};
            this.activeSessions.update(sessions => {
                const next = { ...sessions };
                delete next[id];
                updatedSessions = next;
                return next;
            });

            const remaining = Object.values(updatedSessions);
            console.log('Remaining sessions after delete:', remaining.map(s => s.id));

            if (remaining.length === 0) {
                console.log('No more sessions. Resetting call state to IDLE.');
                this.callInProgress = false;
                this.callState.set(CallState.IDLE);
                this.inboundCallState.set(CallState.IDLE);
                this.callended$.next(true);
                this.isMuted.set(false);
                this.isOnHold.set(false);
                this.currentSession = null;
                this.cleanupAudio();
            } else {
                console.log('Remaining sessions exist. Updating current session and state.');
                this.currentSession = remaining[0].session;
                const hasConnected = remaining.some(s => s.state === CallState.CONNECTED);
                this.callState.set(hasConnected ? CallState.CONNECTED : CallState.RINGING);
            }
        } catch (e) {
            console.error('Logout cleanup error', e);
        } finally {
            this.endingSessionIds.delete(id);
        }
    }

    toggleMute(id?: string) {
        const targetId = id || this.getSessionId(this.currentSession);
        const data = this.activeSessions()[targetId];
        if (!data) return;
        const isMuting = !data.isMuted;
        try {
            if (isMuting) data.session.mute(); else data.session.unmute();
            this.updateSessionState(targetId, { isMuted: isMuting });
            if (targetId === this.getSessionId(this.currentSession)) this.isMuted.set(isMuting);
        } catch (e) { }
    }

    toggleHold(id?: string) {
        const targetId = id || this.getSessionId(this.currentSession);
        const data = this.activeSessions()[targetId];
        if (!data) return;
        const isHolding = !data.isOnHold;
        try {
            if (isHolding) data.session.hold(); else data.session.unhold();
            this.updateSessionState(targetId, { isOnHold: isHolding });
            if (targetId === this.getSessionId(this.currentSession)) this.isOnHold.set(isHolding);
        } catch (e) { }
    }

    hasActiveCall(): boolean { return Object.keys(this.activeSessions()).length > 0; }

    formatCallerId(rawNumber: string): string | null {
        if (!rawNumber) return null;
        let num = rawNumber.replace(/\D/g, '');
        if (num.length === 10) return `1${num}`;
        if (num.length === 11 && num.startsWith('1')) return num;
        if (num.startsWith('440')) return `44${num.slice(3)}`;
        if (num.startsWith('0') && num.length >= 10) return `44${num.slice(1)}`;
        if (num.startsWith('44')) return num;
        return null;
    }

    private attachAudio(session: any) {
        if (session?.sessionDescriptionHandler) {
            session.on('trackAdded', () => {
                const pc = session.sessionDescriptionHandler.peerConnection;
                const remoteStream = new MediaStream();
                pc.getReceivers().forEach((r: any) => r.track && remoteStream.addTrack(r.track));
                const audio = document.getElementById('remoteAudio') as HTMLAudioElement;
                if (audio) { audio.srcObject = remoteStream; audio.play().catch(() => { }); }
            });
        }
    }

    private cleanupAudio() {
        const audio = document.getElementById('remoteAudio') as HTMLAudioElement;
        if (audio) audio.srcObject = null;
    }

    saveCall(session?: any, status?: string, caller_data?: any) {

        let fromNumber: string | null = null;
        let toNumber: string | null = null;
        
        if (session?.direction === 'inbound') {
            fromNumber = caller_data?.caller || session?.callee || caller_data?.phone || null;
            toNumber = this.profileData?.admin_mobile || this.sipInfo?.username || null;
        } else {
            fromNumber = this.profileData?.admin_mobile || this.sipInfo?.username || null;
            toNumber = caller_data?.caller || session?.callee || caller_data?.phone || null;
        }

        const payload = {
            sessionId: session?.sessionId || null,
            direction: session?.direction || null,
            from: fromNumber,
            to: toNumber,
            lead_id: caller_data?.lead_id || null
        };
        this._api.api(payload, 'leads/call_ended').subscribe(() => { });
    }

    sendDTMF(digit: string, id?: string): boolean {
        const targetId = id || this.getSessionId(this.currentSession);
        const session = targetId ? this.activeSessions()[targetId]?.session : this.currentSession;
        if (!session) return false;
        try { session.sendDtmf(digit); return true; } catch (e) { return false; }
    }
}